<script setup lang="ts">
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { vitalSignsApi } from '@/api/vital-signs';
import { type VitalSignRecord } from '@/api/vital-sign-records';

const props = defineProps<{
  vitalSignRecords: VitalSignRecord[];
}>();

const showDialog = defineModel<boolean>();

const { data: vitalSigns } = useQuery({
  queryKey: ['vitalSigns'],
  queryFn: () => vitalSignsApi.index(),
});

const vitalSignsNames = computed(() => {
  if (!vitalSigns.value) return [];

  return vitalSigns.value.map(vitalSign => `${vitalSign.name} (${vitalSign.unit})`).sort();
});

function buildRecordedSignsMap(records: VitalSignRecord[]): Map<string, VitalSignRecord[]> {
  const recordedSigns = new Map<string, VitalSignRecord[]>();

  records.forEach(record => {
    if (!recordedSigns.has(record.vitalSignName)) {
      recordedSigns.set(record.vitalSignName, []);
    }
    recordedSigns.get(record.vitalSignName)!.push(record);
  });

  return recordedSigns;
}

function getLatestRecord(records: VitalSignRecord[]): VitalSignRecord | undefined {
  const sorted = [...records].sort((a, b) =>
    new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
  );

  return sorted[0];
}

const EMPTY_SUMMARY = { formattedValue: '-', humanRecordedAt: '-', recordsCount: 0 };

function buildSignSummary(vitalSignName: string, records?: VitalSignRecord[]) {
  if (!records?.length) {
    return { vitalSignName, ...EMPTY_SUMMARY };
  }

  const latest = getLatestRecord(records);
  if (!latest) {
    return { vitalSignName, ...EMPTY_SUMMARY };
  }

  return {
    vitalSignName,
    formattedValue: latest.formattedValue ?? '',
    humanRecordedAt: latest.humanRecordedAt ?? '',
    recordsCount: records.length,
  };
}

const vitalSignsSummary = computed(() => {
  const hasRecords = props.vitalSignRecords?.length;
  const recordedSigns = hasRecords ?
    buildRecordedSignsMap(props.vitalSignRecords) :
    new Map<string, VitalSignRecord[]>();

  return vitalSignsNames.value.map(name => buildSignSummary(name, recordedSigns.get(name)));
});

function closeDialog() {
  showDialog.value = false;
}
</script>
<template>
  <v-dialog
    v-model="showDialog"
    max-width="800"
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon
          class="mr-2"
          color="primary"
        >
          mdi-heart-pulse
        </v-icon>
        Resumen de Signos Vitales
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="closeDialog"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <v-row>
          <v-col
            v-for="summary in vitalSignsSummary"
            :key="summary.vitalSignName"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              variant="tonal"
              :color="summary.recordsCount > 0 ? 'primary' : 'grey-lighten-3'"
              class="text-center"
            >
              <v-card-text class="pb-2">
                <div class="text-caption text-medium-emphasis">
                  {{ summary.vitalSignName }}
                </div>
                <div
                  class="text-h6 font-weight-bold"
                  :class="summary.recordsCount > 0 ? 'text-primary' : 'text-grey'"
                >
                  {{ summary.formattedValue }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  <span v-if="summary.recordsCount > 0">
                    Último: {{ summary.humanRecordedAt }}
                  </span>
                  <span v-else>
                    Sin registros
                  </span>
                </div>
                <v-chip
                  v-if="summary.recordsCount > 1"
                  size="x-small"
                  variant="outlined"
                  color="primary"
                  class="mt-1"
                >
                  {{ summary.recordsCount }} registros
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
